import React, { useState, useEffect } from 'react'
import { Modal, Form, Button, Row, Col, Card, Badge, Alert } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET } from '../../api/ApiConst'
import apiCaller from '../../api/ApiCaller'

const TableIndexCreateForm = ({ show, onClose, tableId, tableName, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        indexType: 'single', // single, compound, text, geospatial
        fields: [{ field: '', order: 1 }], // 1 for ascending, -1 for descending
        unique: false,
        sparse: false,
        background: true,
        partialFilterExpression: '',
        expireAfterSeconds: '',
        textIndexVersion: 2,
        weights: {},
        defaultLanguage: 'english',
        languageOverride: '',
        collation: {
            locale: 'en',
            strength: 1,
            caseLevel: false,
            numericOrdering: false
        }
    })
    
    const [availableFields, setAvailableFields] = useState([])
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})

    useEffect(() => {
        if (show && tableId) {
            fetchTableFields()
            resetForm()
        }
    }, [show, tableId])

    const fetchTableFields = async () => {
        try {
            const url = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll("{dynamic-table-id}", tableId)
            apiCaller.callPostApi(url, {}, null, (response) => {
                setAvailableFields(response.data || [])
            }, (error) => {
                console.error('Error fetching table fields:', error)
                toast.error('Failed to fetch table fields')
            })
        } catch (error) {
            console.error('Error fetching table fields:', error)
        }
    }

    const resetForm = () => {
        setFormData({
            name: '',
            indexType: 'single',
            fields: [{ field: '', order: 1 }],
            unique: false,
            sparse: false,
            background: true,
            partialFilterExpression: '',
            expireAfterSeconds: '',
            textIndexVersion: 2,
            weights: {},
            defaultLanguage: 'english',
            languageOverride: '',
            collation: {
                locale: 'en',
                strength: 1,
                caseLevel: false,
                numericOrdering: false
            }
        })
        setErrors({})
    }

    const handleClose = () => {
        resetForm()
        onClose()
    }

    const handleIndexTypeChange = (newType) => {
        let newFields = [...formData.fields]
        
        // Reset fields based on index type
        switch (newType) {
            case 'single':
                newFields = [{ field: '', order: 1 }]
                break
            case 'compound':
                if (newFields.length === 1) {
                    newFields = [{ field: '', order: 1 }, { field: '', order: 1 }]
                }
                break
            case 'text':
                newFields = [{ field: '', order: 'text' }]
                break
            case 'geospatial':
                newFields = [{ field: '', order: '2dsphere' }]
                break
        }
        
        setFormData({
            ...formData,
            indexType: newType,
            fields: newFields
        })
    }

    const handleFieldChange = (index, field, value) => {
        const newFields = [...formData.fields]
        newFields[index] = { ...newFields[index], [field]: value }
        setFormData({ ...formData, fields: newFields })
        
        // Clear field-specific errors
        if (errors[`field_${index}`]) {
            const newErrors = { ...errors }
            delete newErrors[`field_${index}`]
            setErrors(newErrors)
        }
    }

    const addField = () => {
        if (formData.indexType === 'single' || formData.indexType === 'text') {
            return // Don't allow adding fields for single/text indexes
        }
        
        const defaultOrder = formData.indexType === 'geospatial' ? '2dsphere' : 1
        setFormData({
            ...formData,
            fields: [...formData.fields, { field: '', order: defaultOrder }]
        })
    }

    const removeField = (index) => {
        const minFields = formData.indexType === 'compound' ? 2 : 1
        if (formData.fields.length > minFields) {
            const newFields = formData.fields.filter((_, i) => i !== index)
            setFormData({ ...formData, fields: newFields })
        }
    }

    const handleWeightChange = (field, weight) => {
        const newWeights = { ...formData.weights }
        if (weight === '') {
            delete newWeights[field]
        } else {
            newWeights[field] = parseInt(weight) || 1
        }
        setFormData({ ...formData, weights: newWeights })
    }

    const validateForm = () => {
        const newErrors = {}
        
        if (!formData.name.trim()) {
            newErrors.name = 'Index name is required'
        }
        
        formData.fields.forEach((field, index) => {
            if (!field.field) {
                newErrors[`field_${index}`] = 'Field is required'
            }
        })
        
        if (formData.expireAfterSeconds && (isNaN(formData.expireAfterSeconds) || formData.expireAfterSeconds <= 0)) {
            newErrors.expireAfterSeconds = 'Expire after seconds must be a positive number'
        }
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }
        
        setLoading(true)
        
        try {
            // TODO: Replace with actual API call when backend supports index creation
            // For now, simulate the API call
            await new Promise(resolve => setTimeout(resolve, 1000))
            
            toast.success('Index created successfully!')
            onSuccess()
            handleClose()
        } catch (error) {
            console.error('Error creating index:', error)
            toast.error('Failed to create index')
        } finally {
            setLoading(false)
        }
    }

    const getIndexPreview = () => {
        if (formData.fields.some(f => f.field)) {
            const fieldsSpec = formData.fields
                .filter(f => f.field)
                .map(f => {
                    if (formData.indexType === 'text') {
                        return `"${f.field}": "text"`
                    } else if (formData.indexType === 'geospatial') {
                        return `"${f.field}": "${f.order}"`
                    } else {
                        return `"${f.field}": ${f.order}`
                    }
                })
                .join(', ')
            
            return `{ ${fieldsSpec} }`
        }
        return 'No fields selected'
    }

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fas fa-database me-2"></i>
                    Create Index for Table: {tableName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Index Name *</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter index name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    isInvalid={!!errors.name}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Index Type</Form.Label>
                                <Form.Select
                                    value={formData.indexType}
                                    onChange={(e) => handleIndexTypeChange(e.target.value)}
                                >
                                    <option value="single">Single Field</option>
                                    <option value="compound">Compound</option>
                                    <option value="text">Text</option>
                                    <option value="geospatial">Geospatial</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Card className="mb-3">
                        <Card.Header>
                            <h6 className="mb-0">Index Fields</h6>
                        </Card.Header>
                        <Card.Body>
                            {formData.fields.map((field, index) => (
                                <Row key={index} className="mb-2">
                                    <Col md={6}>
                                        <Form.Select
                                            value={field.field}
                                            onChange={(e) => handleFieldChange(index, 'field', e.target.value)}
                                            isInvalid={!!errors[`field_${index}`]}
                                        >
                                            <option value="">Select Field</option>
                                            {availableFields.map(f => (
                                                <option key={f.id} value={f.name}>
                                                    {f.name} ({f.type})
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {errors[`field_${index}`]}
                                        </Form.Control.Feedback>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Select
                                            value={field.order}
                                            onChange={(e) => handleFieldChange(index, 'order', e.target.value)}
                                        >
                                            {formData.indexType === 'text' ? (
                                                <option value="text">Text</option>
                                            ) : formData.indexType === 'geospatial' ? (
                                                <>
                                                    <option value="2dsphere">2D Sphere</option>
                                                    <option value="2d">2D</option>
                                                    <option value="geoHaystack">Geo Haystack</option>
                                                </>
                                            ) : (
                                                <>
                                                    <option value={1}>Ascending (1)</option>
                                                    <option value={-1}>Descending (-1)</option>
                                                </>
                                            )}
                                        </Form.Select>
                                    </Col>
                                    <Col md={2}>
                                        <div className="d-flex gap-1">
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                onClick={addField}
                                                disabled={formData.indexType === 'single' || formData.indexType === 'text' || formData.fields.length >= 5}
                                                title={formData.indexType === 'single' || formData.indexType === 'text' ? 'Cannot add fields to single/text indexes' : 'Add field'}
                                            >
                                                <i className="fas fa-plus"></i>
                                            </Button>
                                            {formData.fields.length > (formData.indexType === 'compound' ? 2 : 1) && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => removeField(index)}
                                                    title="Remove field"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </Button>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            ))}
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Header>
                            <h6 className="mb-0">Index Options</h6>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            label="Unique Index"
                                            checked={formData.unique}
                                            onChange={(e) => setFormData({ ...formData, unique: e.target.checked })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            label="Sparse Index"
                                            checked={formData.sparse}
                                            onChange={(e) => setFormData({ ...formData, sparse: e.target.checked })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            label="Background Creation"
                                            checked={formData.background}
                                            onChange={(e) => setFormData({ ...formData, background: e.target.checked })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>TTL (Expire After Seconds)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Leave empty for no TTL"
                                            value={formData.expireAfterSeconds}
                                            onChange={(e) => setFormData({ ...formData, expireAfterSeconds: e.target.value })}
                                            isInvalid={!!errors.expireAfterSeconds}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.expireAfterSeconds}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Header>
                            <h6 className="mb-0">Index Preview</h6>
                        </Card.Header>
                        <Card.Body>
                            <pre className="bg-light p-3 rounded">
                                {getIndexPreview()}
                            </pre>
                        </Card.Body>
                    </Card>

                    <Alert variant="info" className="mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        <strong>Note:</strong> This is a preview of the index creation functionality. 
                        The actual index will be created when the backend supports MongoDB index management.
                    </Alert>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-plus me-2"></i>
                            Create Index
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default TableIndexCreateForm
